import Image from 'components/Common/Image'
import Pane from 'components/Common/Pane'
import Heading from 'LuxKit/Typography/Heading'
import Group from 'components/utils/Group'
import { rem } from 'polished'
import { useCallback, useMemo } from 'react'
import * as React from 'react'
import styled from 'styled-components'
import CarHireOfferTileLocation from './CarHireOfferTileLocation'
import BodyText from 'LuxKit/Typography/BodyText'
import PriceRowPrice from 'LuxKit/PricePoints/PriceRowPrice'
import TextLink from 'LuxKit/TextLink'
import FormatCurrency from 'components/Common/FormatCurrency'
import CarHireDiscountTag from 'components/CarHire/CarHireDiscountTag'
import Clickable from 'components/Common/Clickable'
import noop from 'lib/function/noop'
import { isBundledCarHireEnabled } from 'selectors/carHireSelectors'
import { connect } from 'react-redux'
import { generateLoyaltyPointsCalculatorCarHireOptions } from 'loyalty/lib/pointsCalculation/calculatorOptionsGenerators'
import LoyaltyPointsEarn from 'loyalty/components/Earn/LoyaltyPointsEarn'
import { FormattedMessage, useIntl } from 'react-intl'
import CarHireOrSimilarTooltip from './CarHireOrSimilarTooltip'

const ImageContainer = styled(Pane)`
  display: flex;
  align-items: center;
  justify-content: center;
  width: ${rem(300)};
  height: ${rem(155)};
  position: relative;
`

const SupplierLogoContainer = styled(Pane)`
  position: absolute;
  display: flex;
  align-items: center;
  justify-content: center;
  bottom: ${rem(8)};
  left: ${rem(8)};
`

const OfferContent = styled(Group)`
  height: 100%;
`

const BundleBenefitTagContainer = styled.div`
  position: absolute;
  top: ${rem(8)};
  left: ${rem(8)};
`

const CarImage = styled(Image)`
  filter: brightness(1.25);
`

interface MappedProps {
  isBundledCarHireEnabled: boolean
}

interface Props extends MappedProps {
  offer: App.CarHireOffer
  showBookNow?: boolean
  onOfferClick: (offer: App.CarHireOffer) => void
}

function CarHireUpsellOfferTile(props: Props) {
  const { offer, onOfferClick = noop, isBundledCarHireEnabled, showBookNow } = props
  const intl = useIntl()

  const vehicleImage = useMemo(() => ({
    ...offer.vehicle.image,
    // modify the image URL to utilise car trawlers image options for a better image
    url: `${offer.vehicle.image.url}?w=300&dpr=2`,
  }), [offer.vehicle.image])

  const onClick = useCallback(() => {
    onOfferClick(offer)
  }, [offer, onOfferClick])

  const pointsEarnCalculationRequest = useMemo<Array<App.LoyaltyPointsEarnCalculationRequest | undefined>>(() => {
    // This calculation is only to show the LoyaltyTierPill component below,
    // so there's no need to calculate it if the offer is not eligible for bonus points
    if (!offer.bundledOrder?.loyaltyBonusPointsEarnKey) return []

    return [
      generateLoyaltyPointsCalculatorCarHireOptions(offer, {
        earnKey: offer.bundledOrder.loyaltyBonusPointsEarnKey,
      }),
    ]
  }, [offer])

  const showDiscountTag = offer.bundledOrder?.benefits?.has('discount') && isBundledCarHireEnabled

  const hasDiscount = offer.bundledOrder?.benefits?.has('discount')
  const hasBonusPoints = offer.bundledOrder?.benefits?.has('loyalty_bonus_points')

  return <Clickable onClick={onClick}>
    <Group direction="vertical" gap={16}>
      <ImageContainer border="light-foreground" borderRadius="S" padding={40}>
        <CarImage image={vehicleImage} width="100%" />
        <SupplierLogoContainer border="light-foreground">
          <Image image={offer.vendor.image} width={45} height={24} />
        </SupplierLogoContainer>
        {showDiscountTag && <BundleBenefitTagContainer>
          <CarHireDiscountTag offer={offer} />
        </BundleBenefitTagContainer>}
      </ImageContainer>
      <OfferContent direction="vertical" gap={12} verticalAlign="space-between">
        <Group direction="vertical" gap={4}>
          <CarHireOfferTileLocation offer={offer} />
          <BodyText variant="medium" colour="light-foreground-alt" wrap="word-wrap">
            {offer.guarenteedModel && <Heading as="span" variant="heading3">{offer.vehicle.model}</Heading>}
            {!offer.guarenteedModel && <>
              <FormattedMessage
                id="2r9DQ8aF0efhbdnpu9Kz"
                defaultMessage="<heading>{model}</heading> or similar"
                description="Vehicle model display with 'or similar' suffix when car hire model is not guaranteed"
                values={{
                  model: offer.vehicle.model,
                  heading: (chunks: React.ReactNode) => <Heading as="span" variant="heading3">{chunks}</Heading>,
                }}
              />
              <CarHireOrSimilarTooltip />
            </>}
          </BodyText>
        </Group>
        <Group direction="vertical" gap={0}>
          <PriceRowPrice price={offer.pricePerDay} saleUnit="day" size="L" />
          <Group direction="horizontal" horizontalAlign="space-between">
            {hasDiscount && offer.value > offer.price && <BodyText variant="medium" colour="light-foreground-alt" strikeThrough>
              <FormattedMessage
                id="CSpVgvlzdt-ZNC6JcHM-"
                defaultMessage="Was {price}"
                description="Struck-through original price for car hire upsell offer"
                values={{ price: <FormatCurrency value={offer.valuePerDay} /> }}
              />
            </BodyText>}
            {hasBonusPoints && <LoyaltyPointsEarn calculationRequests={pointsEarnCalculationRequest} calculationType="estimate" hideStatusCredits />}
            {showBookNow && <TextLink weight="regular" size="medium">
              {intl.formatMessage({
                id: 'baH7YTiACPaQvGhI5sFb',
                defaultMessage: 'Book now',
                description: 'Link text to book car hire upsell offer in the car hire booking section',
              })}
            </TextLink>}
          </Group>
        </Group>
      </OfferContent>
    </Group>
  </Clickable>
}

function mapStateToProps(state: App.State) {
  return {
    isBundledCarHireEnabled: isBundledCarHireEnabled(state),
  }
}

export default connect(mapStateToProps)(CarHireUpsellOfferTile)
